import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import axios from "../../axios/axiosconf";
import { formatFecha } from "../../../resources/Helper";
import VistaPreviaImagenClinica from "./VistaPreviaImagenClinica";
import CompararImagenesClinicas from "./CompararImagenesClinicas";
import ModalAyudasDiagnosticasProximamente from "./ModalAyudasDiagnosticasProximamente";
import "./ArchivosClinicos.css";

const FILTROS = [
    { id: "todos", label: "Todos" },
    { id: "imagen", label: "Imágenes" },
    { id: "pdf", label: "Documentos" },
];

const LIMITES_DEFAULT = {
    image_max_upload_bytes: 10 * 1024 * 1024,
    pdf_max_upload_bytes: 5 * 1024 * 1024,
    allowed_mimes: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
};

const MIME_IMAGEN = ["image/jpeg", "image/png", "image/webp"];

export async function verificarCuotaArchivosClinicos(pacienteId) {
    if (!pacienteId) {
        return { limite_bytes: 0, consumo_bytes: 0 };
    }
    try {
        const userdata = JSON.parse(localStorage.getItem("userdata") || "{}");
        const response = await axios.get("/paciente-archivos", {
            headers: { Authorization: "Bearer " + userdata.access_token },
            params: { paciente_id: pacienteId, tipo: "todos", orden: "desc" },
        });
        return response.data.cuota || { limite_bytes: 0, consumo_bytes: 0 };
    } catch {
        return { limite_bytes: 0, consumo_bytes: 0 };
    }
}

function formatBytes(bytes) {
    if (!bytes || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function ArchivosCuotaIndicador({ cuota, className = "" }) {
    const limite = Number(cuota?.limite_bytes || 0);
    const consumo = Number(cuota?.consumo_bytes || 0);
    if (limite <= 0) return null;

    const porcentaje = Math.min(100, Math.round((consumo / limite) * 100));

    return (
        <div className={`archivos-cuota-indicador ${className}`.trim()}>
            <small className="archivos-cuota-text text-muted">
                {formatBytes(consumo)} / {formatBytes(limite)}
            </small>
            <div className="progress archivos-cuota-progress mt-1">
                <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${porcentaje}%` }}
                    aria-valuenow={porcentaje}
                    aria-valuemin={0}
                    aria-valuemax={100}
                />
            </div>
        </div>
    );
}

export function ArchivosControlSubir({ subiendo, disabled, onInputChange, className = "" }) {
    return (
        <span className={`d-inline-flex align-items-center ${className}`.trim()}>
            <label
                className={`archivos-btn-subir mb-0${subiendo || disabled ? " disabled" : ""}`}
                title="Subir archivos (imágenes hasta 10 MB · PDF hasta 5 MB)"
            >
                <i className="fas fa-cloud-upload-alt mr-2" />
                Subir Archivos
                <input
                    type="file"
                    multiple
                    hidden
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={onInputChange}
                    disabled={subiendo || disabled}
                />
            </label>
            {subiendo && <Spinner animation="border" size="sm" className="ml-2" />}
        </span>
    );
}

function validarArchivoAntesSubir(file, cuota, limites) {
    const reglas = limites || LIMITES_DEFAULT;
    const allowed = reglas.allowed_mimes || LIMITES_DEFAULT.allowed_mimes;
    const mime = file.type || "";

    if (!allowed.includes(mime)) {
        return `"${file.name}": tipo no permitido. Solo imágenes (JPG, PNG, WEBP) o PDF.`;
    }

    const esImagen = MIME_IMAGEN.includes(mime);
    const maxBytes = esImagen
        ? Number(reglas.image_max_upload_bytes || LIMITES_DEFAULT.image_max_upload_bytes)
        : Number(reglas.pdf_max_upload_bytes || LIMITES_DEFAULT.pdf_max_upload_bytes);
    const maxMb = (maxBytes / 1024 / 1024).toFixed(0);

    if (file.size > maxBytes) {
        const tam = formatBytes(file.size);
        return `"${file.name}" (${tam}) supera el límite de ${maxMb} MB para ${esImagen ? "imágenes" : "PDF"}.`;
    }

    const limiteCuota = Number(cuota?.limite_bytes || 0);
    const consumoCuota = Number(cuota?.consumo_bytes || 0);
    if (limiteCuota > 0 && consumoCuota + file.size > limiteCuota) {
        return `"${file.name}": no hay espacio suficiente en la cuota de almacenamiento.`;
    }

    return null;
}

export function ArchivosClinicosContenido({
    pacienteId,
    activo = true,
    guardarCambios,
    modoPagina = false,
    onCuotaChange,
    onExposeSubir,
}) {
    const [loading, setLoading] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [progresoSubida, setProgresoSubida] = useState(null);
    const [archivos, setArchivos] = useState([]);
    const [cuota, setCuota] = useState({ limite_bytes: 0, consumo_bytes: 0 });
    const [limites, setLimites] = useState(LIMITES_DEFAULT);
    const [filtro, setFiltro] = useState("todos");
    const [orden, setOrden] = useState("desc");
    const [error, setError] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewNombre, setPreviewNombre] = useState("");
    const [thumbs, setThumbs] = useState({});
    const [renombrandoId, setRenombrandoId] = useState(null);
    const [nuevoNombre, setNuevoNombre] = useState("");
    const [guardandoNombre, setGuardandoNombre] = useState(false);
    const [modoComparar, setModoComparar] = useState(false);
    const [seleccionComparar, setSeleccionComparar] = useState([]);
    const [compararDatos, setCompararDatos] = useState(null);
    const [abriendoComparacion, setAbriendoComparacion] = useState(false);

    const onCuotaChangeRef = useRef(onCuotaChange);
    const pacienteCargadoRef = useRef(null);
    const fetchAbortRef = useRef(null);

    useEffect(() => {
        onCuotaChangeRef.current = onCuotaChange;
    }, [onCuotaChange]);

    const cerrarPreview = useCallback(() => {
        setPreviewUrl((url) => {
            if (url && !Object.values(thumbs).includes(url)) {
                URL.revokeObjectURL(url);
            }
            return null;
        });
        setPreviewNombre("");
    }, [thumbs]);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userdata") || "{}");
        } catch {
            return {};
        }
    }, []);

    const authConfig = useCallback((extra = {}) => ({
        headers: { Authorization: "Bearer " + userdata.access_token },
        ...extra,
    }), [userdata.access_token]);

    const moduloHabilitado = Number(cuota.limite_bytes) > 0;
    const colArchivo = modoPagina ? "col-6 col-md-4 col-lg-3 col-xl-2" : "col-6 col-md-4 col-lg-3";

    const archivosFiltrados = useMemo(() => {
        let lista = [...archivos];
        if (filtro !== "todos") {
            lista = lista.filter((a) => a.tipo === filtro);
        }
        lista.sort((a, b) => {
            const da = new Date(a.created_at).getTime();
            const db = new Date(b.created_at).getTime();
            return orden === "asc" ? da - db : db - da;
        });
        return lista;
    }, [archivos, filtro, orden]);

    const totalImagenes = useMemo(
        () => archivos.filter((a) => a.tipo === "imagen").length,
        [archivos]
    );

    const cargarArchivos = useCallback(async ({ silencioso = false } = {}) => {
        if (!pacienteId || !userdata.access_token) return;

        if (fetchAbortRef.current) {
            fetchAbortRef.current.abort();
        }
        const controller = new AbortController();
        fetchAbortRef.current = controller;

        const yaTieneDatos = pacienteCargadoRef.current === String(pacienteId);
        if (!silencioso && !yaTieneDatos) {
            setLoading(true);
        }
        setError("");

        try {
            const response = await axios.get("/paciente-archivos", authConfig({
                params: { paciente_id: pacienteId, tipo: "todos", orden: "desc" },
                signal: controller.signal,
            }));
            if (controller.signal.aborted) return;

            setArchivos(response.data.archivos || []);
            const nuevaCuota = response.data.cuota || { limite_bytes: 0, consumo_bytes: 0 };
            setCuota(nuevaCuota);
            if (onCuotaChangeRef.current) onCuotaChangeRef.current(nuevaCuota);
            if (response.data.limites) {
                setLimites({ ...LIMITES_DEFAULT, ...response.data.limites });
            }
            pacienteCargadoRef.current = String(pacienteId);
        } catch (err) {
            if (controller.signal.aborted || err.code === "ERR_CANCELED") return;
            const msg = err.response?.data?.error || err.response?.data?.message || "No se pudieron cargar los archivos.";
            setError(msg);
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
            if (fetchAbortRef.current === controller) {
                fetchAbortRef.current = null;
            }
        }
    }, [pacienteId, userdata.access_token, authConfig]);

    const cargarThumbnail = useCallback(async (archivoId) => {
        try {
            const response = await axios.get(`/paciente-archivos/${archivoId}/preview`, authConfig({
                responseType: "blob",
            }));
            const url = URL.createObjectURL(response.data);
            setThumbs((prev) => (prev[archivoId] ? prev : { ...prev, [archivoId]: url }));
        } catch {
            // thumbnail opcional
        }
    }, [authConfig]);

    useEffect(() => {
        if (!activo) {
            if (fetchAbortRef.current) {
                fetchAbortRef.current.abort();
                fetchAbortRef.current = null;
            }
            cerrarPreview();
            setRenombrandoId(null);
            setNuevoNombre("");
            setProgresoSubida(null);
            setModoComparar(false);
            setSeleccionComparar([]);
            setCompararDatos(null);
            setThumbs((prev) => {
                Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
                return {};
            });
            return;
        }
        if (!pacienteId) return;

        const silencioso = pacienteCargadoRef.current === String(pacienteId);
        cargarArchivos({ silencioso });

        return () => {
            if (fetchAbortRef.current) {
                fetchAbortRef.current.abort();
                fetchAbortRef.current = null;
            }
        };
    }, [activo, pacienteId, cargarArchivos, cerrarPreview]);

    useEffect(() => {
        if (!activo) return;
        archivosFiltrados
            .filter((a) => a.tipo === "imagen")
            .forEach((a) => cargarThumbnail(a.id));
    }, [activo, archivosFiltrados, cargarThumbnail]);

    const subirArchivos = async (files) => {
        if (!files?.length || !moduloHabilitado) return;

        const rechazados = [];
        const aceptados = [];
        let consumoSimulado = Number(cuota.consumo_bytes || 0);
        const limiteCuota = Number(cuota.limite_bytes || 0);

        files.forEach((file) => {
            const msg = validarArchivoAntesSubir(file, {
                limite_bytes: limiteCuota,
                consumo_bytes: consumoSimulado,
            }, limites);
            if (msg) {
                rechazados.push(msg);
            } else {
                aceptados.push(file);
                consumoSimulado += file.size;
            }
        });

        if (rechazados.length > 0) {
            setError(rechazados.join(" "));
        } else {
            setError("");
        }

        if (!aceptados.length) return;

        setSubiendo(true);
        let exitos = 0;
        let consumoActual = Number(cuota.consumo_bytes || 0);
        const total = aceptados.length;

        for (let i = 0; i < aceptados.length; i++) {
            const file = aceptados[i];
            const formData = new FormData();
            formData.append("paciente_id", pacienteId);
            formData.append("archivo", file);

            setProgresoSubida({
                indice: i + 1,
                total,
                nombre: file.name,
                porcentaje: 0,
                global: Math.round((i / total) * 100),
            });

            try {
                const response = await axios.post("/paciente-archivos", formData, authConfig({
                    headers: {
                        Authorization: "Bearer " + userdata.access_token,
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (progressEvent) => {
                        const pct = progressEvent.total
                            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                            : 0;
                        const global = Math.round(((i + pct / 100) / total) * 100);
                        setProgresoSubida({
                            indice: i + 1,
                            total,
                            nombre: file.name,
                            porcentaje: pct,
                            global,
                        });
                    },
                }));
                exitos++;
                if (response.data?.cuota) {
                    consumoActual = Number(response.data.cuota.consumo_bytes || consumoActual);
                    setCuota(response.data.cuota);
                    if (onCuotaChangeRef.current) onCuotaChangeRef.current(response.data.cuota);
                } else {
                    consumoActual += file.size;
                    setCuota((prev) => ({ ...prev, consumo_bytes: consumoActual }));
                }
            } catch (err) {
                const msg = err.response?.data?.error || err.response?.data?.message || `Error al subir ${file.name}`;
                setError((prev) => (prev ? `${prev} ${msg}` : msg));
            }
        }

        setProgresoSubida(null);

        if (exitos > 0) {
            if (guardarCambios) {
                guardarCambios(exitos === 1 ? "Archivo subido correctamente" : `${exitos} archivos subidos`);
            }
            await cargarArchivos();
        }
        setSubiendo(false);
    };

    const onInputChange = (e) => {
        subirArchivos(Array.from(e.target.files || []));
        e.target.value = "";
    };

    useEffect(() => {
        if (!modoPagina || !onExposeSubir) return undefined;
        if (!moduloHabilitado) {
            onExposeSubir(null);
            return undefined;
        }
        onExposeSubir({ subiendo, onInputChange, disabled: subiendo });
        return () => onExposeSubir(null);
    }, [modoPagina, onExposeSubir, moduloHabilitado, subiendo]);

    const onDrop = (e) => {
        e.preventDefault();
        subirArchivos(Array.from(e.dataTransfer.files || []));
    };

    const abrirArchivo = async (archivo) => {
        if (archivo.tipo === "pdf") {
            try {
                const response = await axios.get(`/paciente-archivos/${archivo.id}/download`, authConfig({
                    responseType: "blob",
                }));
                const url = URL.createObjectURL(response.data);
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch {
                setError("No se pudo abrir el documento.");
            }
            return;
        }

        if (thumbs[archivo.id]) {
            setPreviewUrl(thumbs[archivo.id]);
            setPreviewNombre(archivo.nombre_original);
            return;
        }

        try {
            const response = await axios.get(`/paciente-archivos/${archivo.id}/preview`, authConfig({
                responseType: "blob",
            }));
            const url = URL.createObjectURL(response.data);
            setPreviewUrl(url);
            setPreviewNombre(archivo.nombre_original);
        } catch {
            setError("No se pudo abrir la imagen.");
        }
    };

    const eliminarArchivo = async (archivo) => {
        if (!window.confirm(`¿Eliminar "${archivo.nombre_original}"?`)) return;
        try {
            await axios.delete(`/paciente-archivos/${archivo.id}`, authConfig());
            if (guardarCambios) guardarCambios("Archivo eliminado");
            await cargarArchivos();
        } catch (err) {
            setError(err.response?.data?.error || "No se pudo eliminar el archivo.");
        }
    };

    const iniciarRenombrar = (archivo) => {
        setRenombrandoId(archivo.id);
        setNuevoNombre(archivo.nombre_original);
        setError("");
    };

    const cancelarRenombrar = () => {
        setRenombrandoId(null);
        setNuevoNombre("");
    };

    const guardarRenombrar = async (archivoId) => {
        const nombre = nuevoNombre.trim();
        if (!nombre) {
            setError("El nombre no puede estar vacío.");
            return;
        }
        setGuardandoNombre(true);
        try {
            await axios.put(`/paciente-archivos/${archivoId}`, { nombre_original: nombre }, authConfig());
            if (guardarCambios) guardarCambios("Archivo renombrado");
            cancelarRenombrar();
            await cargarArchivos();
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.message || "No se pudo renombrar el archivo.");
        } finally {
            setGuardandoNombre(false);
        }
    };

    const descargarZip = async () => {
        try {
            const response = await axios.get(`/paciente-archivos/paciente/${pacienteId}/zip`, authConfig({
                responseType: "blob",
            }));
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `archivos-paciente-${pacienteId}.zip`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.response?.data?.error || "No se pudo generar el ZIP.");
        }
    };

    const descargarArchivo = async (archivo) => {
        try {
            const response = await axios.get(`/paciente-archivos/${archivo.id}/download`, authConfig({
                responseType: "blob",
            }));
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = archivo.nombre_original || `archivo-${archivo.id}`;
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            setError("No se pudo descargar el archivo.");
        }
    };

    const cambiarFiltro = (id, activar) => {
        if (activar) {
            setFiltro(id);
            return;
        }
        if (filtro === id) {
            setFiltro("todos");
        }
    };

    const cancelarModoComparar = () => {
        setModoComparar(false);
        setSeleccionComparar([]);
    };

    const toggleSeleccionComparar = (archivoId) => {
        setSeleccionComparar((prev) => {
            if (prev.includes(archivoId)) {
                return prev.filter((id) => id !== archivoId);
            }
            if (prev.length < 2) {
                return [...prev, archivoId];
            }
            return [prev[1], archivoId];
        });
    };

    const obtenerUrlPreview = async (archivoId) => {
        if (thumbs[archivoId]) {
            return thumbs[archivoId];
        }
        const response = await axios.get(`/paciente-archivos/${archivoId}/preview`, authConfig({
            responseType: "blob",
        }));
        const url = URL.createObjectURL(response.data);
        setThumbs((prev) => (prev[archivoId] ? prev : { ...prev, [archivoId]: url }));
        return url;
    };

    const abrirComparacion = async () => {
        if (seleccionComparar.length !== 2) return;

        const archivosSeleccionados = seleccionComparar
            .map((id) => archivos.find((a) => a.id === id))
            .filter(Boolean);

        if (archivosSeleccionados.length !== 2) return;

        setAbriendoComparacion(true);
        setError("");

        try {
            const [izq, der] = await Promise.all(
                archivosSeleccionados.map(async (archivo) => ({
                    nombre: archivo.nombre_original,
                    url: await obtenerUrlPreview(archivo.id),
                }))
            );

            setCompararDatos({ imagenIzquierda: izq, imagenDerecha: der });
            setModoComparar(false);
            setSeleccionComparar([]);
        } catch {
            setError("No se pudieron cargar las imágenes para comparar.");
        } finally {
            setAbriendoComparacion(false);
        }
    };

    const manejarClickImagen = (archivo) => {
        if (modoComparar && archivo.tipo === "imagen") {
            toggleSeleccionComparar(archivo.id);
            return;
        }
        abrirArchivo(archivo);
    };

    return (
        <>
            <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
                <div className="archivos-clinicos-toolbar d-flex flex-wrap align-items-center mb-2">
                    {moduloHabilitado && (
                        <div className="archivos-toolbar-left d-flex align-items-center flex-wrap mb-1">
                            <div className="d-flex align-items-center mr-2">
                                <span className="archivos-orden-label mr-2">Ordenar</span>
                                <select
                                    className="form-control form-control-sm archivos-orden-select"
                                    value={orden}
                                    onChange={(e) => setOrden(e.target.value)}
                                >
                                    <option value="desc">Más recientes primero</option>
                                    <option value="asc">Más antiguos primero</option>
                                </select>
                            </div>
                            <button
                                type="button"
                                className="archivos-btn-icon mr-1"
                                title="Descargar ZIP"
                                onClick={descargarZip}
                                disabled={!archivos.length || subiendo}
                            >
                                <i className="fas fa-file-archive" />
                            </button>
                            {totalImagenes >= 2 && (
                                <button
                                    type="button"
                                    className={`archivos-btn-icon mr-1${modoComparar ? " archivos-btn-icon--activo" : ""}`}
                                    title="Comparar dos imágenes"
                                    onClick={() => (modoComparar ? cancelarModoComparar() : setModoComparar(true))}
                                    disabled={subiendo}
                                >
                                    <i className="fas fa-columns" />
                                </button>
                            )}
                        </div>
                    )}

                    {moduloHabilitado && !modoPagina && (
                        <div className="archivos-toolbar-center d-flex align-items-center justify-content-center flex-grow-1 mb-1 px-2">
                            <ArchivosControlSubir
                                subiendo={subiendo}
                                disabled={subiendo}
                                onInputChange={onInputChange}
                            />
                        </div>
                    )}

                    <div className="archivos-toolbar-right d-flex align-items-center flex-wrap ms-auto mb-1">
                        <div className="archivos-filtros-switch-row">
                            {FILTROS.map((f) => {
                                const inputId = `filtro-archivo-${f.id}`;
                                return (
                                    <label key={f.id} className="archivos-filtro-switch-item" htmlFor={inputId}>
                                        <span>{f.label}</span>
                                        <span className="custom-switch custom-switch-sm archivos-switch mb-0">
                                            <input
                                                id={inputId}
                                                type="checkbox"
                                                checked={filtro === f.id}
                                                onChange={(e) => cambiarFiltro(f.id, e.target.checked)}
                                            />
                                            <span className="slider round" />
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {subiendo && progresoSubida && (
                    <div className="mb-2 p-2 rounded border bg-light">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-muted text-truncate me-2" style={{ maxWidth: "70%" }}>
                                Subiendo {progresoSubida.indice} de {progresoSubida.total}: {progresoSubida.nombre}
                            </small>
                            <small className="font-weight-bold">{progresoSubida.global}%</small>
                        </div>
                        <div className="progress mb-1 archivos-progress-global" style={{ height: 10 }}>
                            <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style={{ width: `${progresoSubida.global}%` }}
                            />
                        </div>
                        <div className="progress archivos-progress-archivo" style={{ height: 4 }}>
                            <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${progresoSubida.porcentaje}%` }}
                            />
                        </div>
                    </div>
                )}

                {error && <div className="alert alert-danger py-2 mb-2">{error}</div>}

                {modoComparar && (
                    <div className="archivos-comparar-bar alert alert-info py-2 mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span className="small mb-0">
                            <i className="fas fa-columns mr-1" />
                            Seleccione 2 imágenes para comparar ({seleccionComparar.length}/2)
                        </span>
                        <div className="archivos-comparar-acciones d-flex align-items-center">
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary archivos-btn-comparar-cancelar"
                                onClick={cancelarModoComparar}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={abrirComparacion}
                                disabled={seleccionComparar.length !== 2 || abriendoComparacion}
                            >
                                {abriendoComparacion ? (
                                    <><Spinner animation="border" size="sm" className="mr-1" />Cargando...</>
                                ) : (
                                    <>Ver Comparación</>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {loading && !archivos.length ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : archivosFiltrados.length === 0 && !loading ? (
                    <p className="text-muted text-center py-3">No hay archivos para mostrar.</p>
                ) : (
                    <div className="row">
                        {archivosFiltrados.map((archivo) => (
                            <div key={archivo.id} className={`${colArchivo} mb-3`}>
                                <div className="card h-100 archivos-card-archivo">
                                    <div
                                        className={`card-img-top d-flex align-items-center justify-content-center position-relative${modoComparar && archivo.tipo === "imagen" ? " archivos-card-img--comparar" : ""}${seleccionComparar.includes(archivo.id) ? " archivos-card-img--seleccionada" : ""}`}
                                        style={{ height: modoPagina ? 160 : 140, background: "#f1f3f5", cursor: "pointer", overflow: "hidden" }}
                                        onClick={() => manejarClickImagen(archivo)}
                                    >
                                        {archivo.tipo === "imagen" && modoComparar && (
                                            <button
                                                type="button"
                                                className={`archivos-btn-seleccion-comparar${seleccionComparar.includes(archivo.id) ? " active" : ""}`}
                                                title={seleccionComparar.includes(archivo.id) ? "Quitar de la comparación" : "Seleccionar para comparar"}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleSeleccionComparar(archivo.id);
                                                }}
                                            >
                                                <i className={`fas ${seleccionComparar.includes(archivo.id) ? "fa-check-circle" : "fa-plus-circle"}`} />
                                            </button>
                                        )}
                                        {archivo.tipo === "imagen" && thumbs[archivo.id] ? (
                                            <img src={thumbs[archivo.id]} alt={archivo.nombre_original} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "cover" }} />
                                        ) : archivo.tipo === "pdf" ? (
                                            <i className="fas fa-file-pdf fa-3x text-danger" />
                                        ) : (
                                            <i className="fas fa-image fa-3x text-secondary" />
                                        )}
                                    </div>
                                    <div className="card-body p-2">
                                        {renombrandoId === archivo.id ? (
                                            <div className="mb-1">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm mb-1"
                                                    value={nuevoNombre}
                                                    maxLength={255}
                                                    onChange={(e) => setNuevoNombre(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") guardarRenombrar(archivo.id);
                                                        if (e.key === "Escape") cancelarRenombrar();
                                                    }}
                                                    autoFocus
                                                    disabled={guardandoNombre}
                                                />
                                                <div className="d-flex">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm archivos-btn-guardar text-white mr-1"
                                                        onClick={() => guardarRenombrar(archivo.id)}
                                                        disabled={guardandoNombre}
                                                    >
                                                        {guardandoNombre ? <Spinner animation="border" size="sm" /> : "Guardar"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={cancelarRenombrar}
                                                        disabled={guardandoNombre}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="small font-weight-bold text-truncate" title={archivo.nombre_original}>{archivo.nombre_original}</div>
                                        )}
                                        <div className="small text-muted">{formatFecha(new Date(archivo.created_at))} · {formatBytes(archivo.tamano_bytes)}</div>
                                        <div className="mt-1 d-flex align-items-center">
                                            {renombrandoId !== archivo.id && (
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm archivos-link-renombrar p-0 mr-2"
                                                    onClick={() => iniciarRenombrar(archivo)}
                                                >
                                                    Renombrar
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm archivos-link-descargar p-0 mr-2"
                                                title="Descargar"
                                                onClick={() => descargarArchivo(archivo)}
                                            >
                                                <i className="fas fa-download" />
                                            </button>
                                            <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => eliminarArchivo(archivo)}>
                                                Eliminar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <VistaPreviaImagenClinica
                show={!!previewUrl}
                onHide={cerrarPreview}
                imageUrl={previewUrl}
                nombre={previewNombre}
            />

            <CompararImagenesClinicas
                show={!!compararDatos}
                onHide={() => setCompararDatos(null)}
                imagenIzquierda={compararDatos?.imagenIzquierda}
                imagenDerecha={compararDatos?.imagenDerecha}
            />
        </>
    );
}

function ModalArchivosClinicos({ show, onHide, guardarCambios }) {
    const queryParams = useLocation().search;
    const pacienteId = new URLSearchParams(queryParams).get("id");
    const [cuotaHeader, setCuotaHeader] = useState({ limite_bytes: 0, consumo_bytes: 0 });
    const [verificandoCuota, setVerificandoCuota] = useState(false);
    const [cuotaLista, setCuotaLista] = useState(false);

    const moduloHabilitado = Number(cuotaHeader.limite_bytes) > 0;

    useEffect(() => {
        if (!show || !pacienteId) {
            setCuotaLista(false);
            return undefined;
        }

        let activo = true;
        setVerificandoCuota(true);
        setCuotaLista(false);

        verificarCuotaArchivosClinicos(pacienteId)
            .then((cuota) => {
                if (!activo) return;
                setCuotaHeader(cuota);
                setCuotaLista(true);
            })
            .finally(() => {
                if (activo) setVerificandoCuota(false);
            });

        return () => {
            activo = false;
        };
    }, [show, pacienteId]);

    if (!show) {
        return null;
    }

    if (verificandoCuota || !cuotaLista) {
        return (
            <Modal show={show} onHide={onHide} centered>
                <Modal.Body className="text-center py-5">
                    <Spinner animation="border" />
                </Modal.Body>
            </Modal>
        );
    }

    if (!moduloHabilitado) {
        return <ModalAyudasDiagnosticasProximamente show={show} onHide={onHide} />;
    }

    const abrirEnPestanaModal = () => {
        const url = `${window.location.origin}${window.location.pathname}#/ayudas-diagnosticas?id=${pacienteId}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            scrollable
            className="modal-archivos-clinicos-root"
            dialogClassName="modal-archivos-clinicos"
        >
            <Modal.Header className="py-2 archivos-modal-header">
                <div className="d-flex align-items-start justify-content-between w-100">
                    <div className="archivos-modal-header-main pe-3">
                        <Modal.Title className="mb-0 h5">
                            <i className="fas fa-images me-2" />
                            Ayudas Diagnósticas
                        </Modal.Title>
                        {moduloHabilitado && (
                            <ArchivosCuotaIndicador cuota={cuotaHeader} className="mt-1" />
                        )}
                    </div>
                    <div className="archivos-modal-header-actions d-flex align-items-center flex-shrink-0">
                        <button
                            type="button"
                            className="archivos-btn-icon archivos-btn-header-icon mr-1"
                            title="Abrir en pestaña completa"
                            onClick={abrirEnPestanaModal}
                        >
                            <i className="fas fa-external-link-alt" />
                        </button>
                        <button type="button" className="close archivos-modal-close" aria-label="Cerrar" onClick={onHide}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="pt-2">
                {show && (
                    <ArchivosClinicosContenido
                        pacienteId={pacienteId}
                        activo={show}
                        guardarCambios={guardarCambios}
                        onCuotaChange={setCuotaHeader}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalArchivosClinicos;
